// SPDX-License-Identifier: LicenseRef-Proprietary

import React from 'react';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div 
      className="help-modal-backdrop" 
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="help-modal-title"
    >
      <div className="help-modal-content">
        <div className="help-modal-header">
          <h2 id="help-modal-title">Decision Tree Builder Help</h2>
          <button 
            className="help-modal-close"
            onClick={onClose}
            aria-label="Close help modal"
          >
            ×
          </button>
        </div>
        
        <div className="help-modal-body">
          <section className="help-section">
            <h3>Primary Workflow</h3>
            <ol>
              <li><strong>Create/Load:</strong> Start with a blank tree or load a template</li>
              <li><strong>Build Structure:</strong> Add nodes and connect them in the outline</li>
              <li><strong>Configure Data:</strong> Set probabilities, costs, and QALYs in the inspector</li>
              <li><strong>Validate:</strong> Check for errors using the validation badge</li>
              <li><strong>Export:</strong> Save as JSON, export as SVG/PNG, or share via link</li>
            </ol>
          </section>

          <section className="help-section">
            <h3>Node Types</h3>
            <div className="node-types">
              <div className="node-type">
                <div className="node-type-icon decision">□</div>
                <div>
                  <strong>Decision</strong> - Represents a choice point. Must have at least 2 children.
                </div>
              </div>
              <div className="node-type">
                <div className="node-type-icon chance">○</div>
                <div>
                  <strong>Chance</strong> - Represents probabilistic outcomes. Probabilities must sum to 1.0.
                </div>
              </div>
              <div className="node-type">
                <div className="node-type-icon terminal">◇</div>
                <div>
                  <strong>Terminal</strong> - Final outcome with cost (€) and QALY values.
                </div>
              </div>
            </div>
          </section>

          <section className="help-section">
            <h3>Quick Start</h3>
            <ol>
              <li>Click "Load template" to start with a vaccination example</li>
              <li>Select nodes in the outline to edit their properties</li>
              <li>Use the inspector to modify labels, probabilities, costs, and QALYs</li>
              <li>Toggle overlays to show/hide probabilities, costs, and QALYs on the visualization</li>
              <li>Check the validation badge for any issues that need attention</li>
            </ol>
          </section>
        </div>
      </div>
    </div>
  );
};

export default HelpModal;
